import {useParams} from "react-router-dom";
import React, { useState, useEffect } from "react";
import {useSelector} from "react-redux";
import ReservationInfo from "./ReservationInfo";
import PaymentMethods from "./PaymentMethods";
import DietPreference from "./DietPreference";
import classNames from "classnames";
import {FormProvider, useForm} from "react-hook-form";
import NewsletterCheckbox from "./payment/NewsletterCheckbox";
import ReservationTimeoutModal from "./ReservationTimeoutModal";
import useReservationTimeout from "../hooks/useReservationTimeout";
import useBookingStepGuard from "../hooks/useBookingStepGuard";
import TenantService from "../services/TenantService";

export default function Book() {
    const {id} = useParams();
    const stepReady = useBookingStepGuard('book');
    const travelers = useSelector(state => state.travelers);
    const categories = travelers.categories || {};
    const { currentTrain: train } = useSelector(state => state.trains);
    const formOptions = {};
    const methods =  useForm(formOptions);
    const {register, formState: { errors }} = methods;

    // Manage reservation timeout
    const { showWarning, timeRemaining, handleExtend, handleTimeout } = useReservationTimeout(id);

    // Check if newsletter integration is active for this tenant
    const [mailchimpActive, setMailchimpActive] = useState(false);
    useEffect(() => {
        TenantService.getTenantInfo()
            .then(response => {
                setMailchimpActive(response.data.mailchimp_active || false);
            })
            .catch(() => {
                setMailchimpActive(false);
            });
    }, []);

    const phoneRegex = /^(?!\+[^\d]+)\+?(?!.*[-\s]{2,})(?!.*[\(\)-]{2,})(?!.*\([^\d]+\))(?!.*\-$)(?!(?:.*(\(.+\))){2,})(?!.*(\(\s|\s\)))(?=.*\(\d+\))?(?!.*\(.*\()(?!.*\).*[\)\(]{1})[0-9\(\)\-\s]{5,32}?$/;
    const emailRegex1 = /^(?!\.)(?!.*@-)(?!.*\.@|.*@\.)(?!.*[\.\-]{2,})[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
    const emailRegexRFC5322 = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    // Build a flat list of all travelers with their category info.
    // A bundle category (with a `composition`) expands into one passenger row per
    // composition seat, all sharing a `bundleId` so the backend prices the bundle
    // once and splits it across its seats.
    const buildTravelersList = () => {
        const list = [];
        Object.keys(categories).forEach((categoryKey) => {
            const category = categories[categoryKey];
            for (let i = 0; i < category.count; i++) {
                if (category.composition) {
                    const bundleId = `${categoryKey}-${i}`;
                    category.composition.forEach((entry) => {
                        const { type } = entry;
                        const member = categories[type] || {};
                        // Fixed entries use `count`; variable entries (e.g. children
                        // in a family ticket) use the chosen `variableCounts`.
                        const count = entry.count != null
                            ? entry.count
                            : (category.variableCounts?.[type] ?? 0);
                        for (let j = 0; j < count; j++) {
                            list.push({
                                categoryKey,
                                name: member.name ?? category.name,
                                description: member.description, // adult/child description → Ticket.type + price validity
                                bundleId,
                                bundleCategory: categoryKey,
                                bundleLabel: category.name,
                            });
                        }
                    });
                } else {
                    list.push({
                        categoryKey,
                        name: category.name,
                        description: category.description,
                    });
                }
            }
        });
        return list;
    };

    const travelersList = buildTravelersList();

    const renderTravelerForm = (traveler, index, isFirst) => {
        const showContactFields = isFirst;
        const showCategoryLabel = !train?.isFullPriceOnly;

        return (
            <div key={index}>
                <div className={classNames("relative", {
                    "mt-6": index !== 0,
                    "mb-6": index !== travelers.total - 1
                })}>
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className="w-full border-t border-gray-300"/>
                    </div>
                    <div className="relative flex justify-center">
                        <span className="px-2 bg-white text-sm text-gray-500">
                            Resenär {index + 1} {showCategoryLabel && (<>{traveler.name} ({traveler.description})</>)}
                        </span>
                    </div>
                </div>
                <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6 sm:col-span-3">
                        <label htmlFor={`travelers.${index}.firstName`}
                               className="block text-sm font-medium text-gray-700">
                            Förnamn
                        </label>
                        <input
                            {...register(`travelers.${index}.firstName`, {required: true})}
                            type="text"
                            autoComplete="given-name"
                            className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                        {errors?.travelers?.[index]?.firstName && (
                            <p className="mt-2 text-sm text-red-600">Förnamn måste anges.</p>)}
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                        <label htmlFor={`travelers.${index}.lastName`}
                               className="block text-sm font-medium text-gray-700">
                            Efternamn
                        </label>
                        <input
                            {...register(`travelers.${index}.lastName`, {required: true})}
                            type="text"
                            autoComplete="family-name"
                            className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                        {errors?.travelers?.[index]?.lastName && (
                            <p className="mt-2 text-sm text-red-600">Efternamn måste anges.</p>)}
                    </div>

                    {showContactFields && (
                        <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="email"
                                   className="block text-sm font-medium text-gray-700">
                                E-postadress
                            </label>
                            <input
                                {...register(`email`, {
                                    required: true,
                                    pattern: emailRegex1,
                                    minLength: 6,
                                    maxLength: 255
                                })}
                                type="text"
                                autoComplete="email"
                                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                            {errors.email && (
                                <p className="mt-2 text-sm text-red-600">E-postadress måste anges.</p>)}
                        </div>
                    )}
                    {showContactFields && (
                        <div className="col-span-6 sm:col-span-3">
                            <label className="block text-sm font-medium text-gray-700">
                                Telefon
                            </label>
                            <input
                                {...register('phone', {required: true, pattern: phoneRegex})}
                                type="text"
                                autoComplete="phone"
                                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                            {errors.phone && (
                                <p className="mt-2 text-sm text-red-600">Telefon måste anges.</p>)}
                        </div>
                    )}
                    <div className="col-span-12 sm:col-span-4">
                        <DietPreference travelerIndex={index} />
                    </div>
                </div>
                <input
                    {...register(`travelers.${index}.description`, {required: true})}
                    type="hidden"
                    value={traveler.description}
                />
                <input
                    {...register(`travelers.${index}.bundleId`)}
                    type="hidden"
                    value={traveler.bundleId ?? ''}
                />
                <input
                    {...register(`travelers.${index}.bundleCategory`)}
                    type="hidden"
                    value={traveler.bundleCategory ?? ''}
                />
            </div>
        );
    };

    // After a reload the booking state is gone; the guard redirects to the
    // step that rebuilds it instead of rendering a broken checkout.
    if (!stepReady) {
        return null;
    }

    return (
        <>
            <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-center text-base font-semibold text-indigo-600 tracking-wide uppercase">Betala</h3>
                    <FormProvider {...methods} >
                        <div className="space-y-6">
                            <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
                                <div className="md:grid md:grid-cols-3 md:gap-6">
                                    <div className="md:col-span-1">
                                        <h3 className="text-lg font-medium leading-6 text-gray-900">Resenärer</h3>
                                        <p className="mt-1 text-sm text-gray-500">Bokningsbekräftelse, kvitto och trafikinformation skickas till den första resenären i listan, som blir kontaktperson.</p>
                                    </div>
                                    <div className="mt-5 md:mt-0 md:col-span-2">
                                        <form>
                                            {travelersList.map((traveler, index) =>
                                                renderTravelerForm(traveler, index, index === 0)
                                            )}
                                        </form>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
                                <div className="md:grid md:grid-cols-3 md:gap-6">
                                    <div className="md:col-span-1">
                                        <h3 className="text-lg font-medium leading-6 text-gray-900">Sammanfattning</h3>
                                        <p className="mt-1 text-sm text-gray-500">Granska att din bokning stämmer vad gäller resmål, resenärer och station.</p>
                                    </div>
                                    <div className="mt-5 md:mt-0 md:col-span-2">
                                        <ReservationInfo checkout={true} />
                                    </div>
                                </div>
                            </div>

                            {mailchimpActive && (
                                <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
                                    <div className="md:grid md:grid-cols-3 md:gap-6">
                                        <div className="md:col-span-1">
                                            <h3 className="text-lg font-medium leading-6 text-gray-900">Nyhetsbrev & erbjudanden</h3>
                                            <p className="mt-1 text-sm text-gray-500">Ta del av nyheter, resor och unika erbjudanden via e-post och/eller sms.</p>
                                        </div>
                                        <div className="mt-5 md:mt-0 md:col-span-2">
                                            <NewsletterCheckbox />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
                                <div className="md:grid md:grid-cols-3 md:gap-6">
                                    <div className="md:col-span-1">
                                        <h3 className="text-lg font-medium leading-6 text-gray-900">Betalsätt</h3>
                                        <p className="mt-1 text-sm text-gray-500">Betala via säkert med kort online. Ange ev. rabattkod innan du går till betalningen.</p>
                                    </div>
                                    <div className="mt-5 md:mt-0 md:col-span-2">
                                        <PaymentMethods />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </FormProvider>
                </div>
            </div>

            <ReservationTimeoutModal
                isOpen={showWarning}
                timeRemaining={timeRemaining}
                onExtend={handleExtend}
                onTimeout={handleTimeout}
            />
        </>
    )
}
